/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useRef, useCallback, useEffect, useState } from "react";

// antd:
import { Form } from "antd";
import JoditEditor from "jodit-react";
// import DOMPurify from "dompurify";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// import JoditEditor from "../react/jodit-react/src/index.ts";

// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getAppState, getTheme, getCompact, getLng, getDict, getUser, changeAppState } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tlng, Tpermissions} from "../../types/easyTypes.ts";

type JoditType = {
  readonly:boolean,
  disabled?:boolean,
  iframe?:boolean,
  iframeStyle?:string,
  placeholder?:string,
  width?:string | number,
  height?:string | number,
  minHeight?:string | number,
  maxHeight?:string | number,
  buttons?:Array<string>,
  controls?:{[key:string]:any},
  extraButtons?:Array<{[key:string]:any}>,
  removeButtons?:Array<string>,
  disablePlugins?:Array<string>,
  extraPlugins?:Array<string>,
  activeButtonsInReadOnly?:Array<string>,
  commandToHotkeys?:{[key:string]:any},
  toolbarButtonSize:"tiny" | "xsmall" | "small" | "middle" | "large",
  theme?:"dark" | "default",
  language?:"en_US" | "ru_RU" | "auto",
  // i18n?:string,
  debugLanguage?:boolean,
  zIndex?:number,
  tabIndex?:number,
  inline?:boolean,
  className?:string,
  containerStyle?:CSS.Properties,
  toolbar?:string | boolean,
  statusbar?:boolean,
  showXPathInStatusbar?:boolean,
  useSplitMode?:boolean,
  colorPickerDefaultTab?:"background" | "color",
  imageDefaultWidth?:number,
  fullsize?:boolean,
  limitWords?:number | false,
  limitChars?:number | false,
  defaultLineHeight?:number,
  defaultActionOnPasteFromWord?:null | "insert_as_html" | "insert_clear_html" | "insert_as_text" | "insert_only_text",
  hidePoweredByJodit?:boolean,
  tableAllowCellResize?:boolean,
  uploader?:{[key:string]:any},
  events?:{[key:string]:any},
  link?:{[key:string]:any},
  enableDragAndDropFileToEditor?:boolean,
  toolbarAdaptive?:boolean,
  toolbarSticky?:boolean,
  addNewLine?:boolean,
  useSearch?:boolean
}

const { rz } = window;

// const cleanHtml = (value) => (DOMPurify.sanitize(value, {
//  USE_PROFILES: { html: true }, // Разрешает только HTML-теги
//  ADD_ATTR: ["target"],
//  ALLOWED_TAGS: ["p", "strong", "em", "table", "tr", "td"] // Какие теги оставить
// }));

const RZJoditEditor:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZJoditEditor.displayName = "JoditEditor";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const theme = useAppSelector(getTheme);
  const isCompact = useAppSelector(getCompact);
  const lng = useAppSelector(getLng);
  const dct = useAppSelector(getDict);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, align} = attributes; // onClick, onChange, onRightClick, onHover, onLoad, extraButtons
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath];
  const {borders} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);
  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    return Boolean(parentState.name === "form");
  }, [appState, fullPath]);

  const [loaded, setAlreadyLoaded] = useState(false);
  const editor = useRef(null); // Get Jodit API

  // console.log("Rerender Jodit " + rz.dateToString(new Date(), "hh:mm:ss"));

  /*
  const cursorPositionRef = useRef(null); // Store cursor position
  const restoreCursorPosition = () => {
    if (cursorPositionRef && cursorPositionRef.current) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        // @ts-ignore
        const curSelection = selection.getRangeAt(0).cloneRange(), oldSelection = cursorPositionRef.current.cloneRange();
        // curSelection.setStart(curSelection.startContainer, oldSelection.startOffset + 1);
        // curSelection.setEnd(curSelection.endContainer, oldSelection.endOffset + 1);
        console.log("restoreCursorPosition", oldSelection);
        selection.removeAllRanges();
        selection.addRange(oldSelection);
      }
    }
  };
  const saveCursorPosition = () => {
    if (editor && editor.current) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const curSelection = selection.getRangeAt(0).cloneRange();
        // @ts-ignore
        cursorPositionRef.current = curSelection;
        console.log("saveCursorPosition", cursorPositionRef.current);
      }
    }
  };
  */

  // Set "Jodit Already Loaded"
  useEffect(() => {
    // @ts-ignore
    if (editor.current && editor.current.isReady && !loaded) { setAlreadyLoaded(true) }
    // @ts-ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(editor.current && editor.current.isReady)]);

  // Set new value using Jodit API if elState.value was changed
  useEffect(() => {
    if (loaded) {
      let curValue = "", err = "";
      try {
        // @ts-ignore
        curValue = (editor.current && editor.current.getEditorValue && editor.current.getEditorValue()) || "";
      }
      catch (err1) { err = err1 }
      // console.log({curValue, stateValue: elState.value});
      // @ts-ignore
      if (!err && curValue !== elState.value && !rz.isUndefinedOrNull(elState.value)) { editor.current.setEditorValue(((elState.value || "").startsWith("<") ? "" : "<p>") + (elState.value || "") + ((elState.value || "").endsWith(">") ? "" : "<br></p>")) }
    }
  }, [elState.value, loaded]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title2 = useMemo<string | undefined>(() => (elState?.attributes["title2_" + lng] || elState?.attributes.title2 || elState?.attributes["title2_" + rlng] || attributes["title2_" + lng] || attributes.title2 || attributes["title2_" + rlng] || ""), [elState?.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng] || ""), [elState.attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const placeholder = useMemo<string | undefined>(() => (elState.attributes["placeholder_" + lng] || elState.attributes.placeholder || elState.attributes["placeholder_" + rlng] || ""), [elState.attributes, lng]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    // if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    if (borders && !rz.arrContains(userClss, /^rz-borders-/igm)) {
      borders.split(" ").filter((ff) => ((/^(light|dark|l1|l2|l3|l4|l5|d1|d2|d3|d4|d5|hover)$/igm).test(ff)))
        .forEach((ff:string) => { if (!~userClss.indexOf("rz-borders-" + ff)) { userClss.push("rz-borders-" + ff) } });
    }
    return userClss;
  }, [elState?.userClass, borders]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    return est;
  }, [elState.userStyle, elState?.visible, permissions]);

  const extraButtonClick = useCallback(({btnName, fncName, jeditor, current, control, value}:{btnName:string, fncName:string, jeditor:any, current:any, control:any, value:string}) => {
    // console.log({fncName, jeditor, current, control});

    if (fnc.config?.isDev) console.log({action: "<Jodit key='" + key + "'> -> " + fncName + "({btnName: '" + btnName + "', value: '" + value + "'}) -> "});

    if (rootNamespace && fncName && rootNamespace[fncName]) {
      const clientFNC = rootNamespace[fncName];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, btnName, value, jeditor, current, control}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, btnName, value, jeditor, current, control}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, rootNamespace, attributes, rootAttributes.key, user, lng, dct, dispatch]);

  const jconfig = useMemo<JoditType>(() => {
    const typ:JoditType = {
      readonly: false, disabled: elState.disabled || permissions.disabled, placeholder,
      defaultLineHeight: 1.2,
      toolbarButtonSize: (isCompact ? "xsmall" : "middle"), theme: (theme === "dark" ? "dark" : "default"),
      toolbar: true, // Показать toolbar
      toolbarAdaptive: false, toolbarSticky: true,
      fullsize: false, // Во весь экран или нет

      statusbar: false, // Показать statusbar
      hidePoweredByJodit: true,
      showXPathInStatusbar: false, // Скрыть html-путь на statusbar
      addNewLine: false, useSearch: false,
      disablePlugins: ["mobile", "paste", "stat"],
      // removeButtons: ["about", "print", "file"],
      colorPickerDefaultTab: "color",
      tableAllowCellResize: false,
      uploader: { insertImageAsBase64URI: true },
      activeButtonsInReadOnly: ["source", "fullsize", "print"],
      language: (lng === "ru" ? "ru_RU" : "en_US"),
      link: {noFollowCheckbox: false, modeClassName: false},
      enableDragAndDropFileToEditor: false,
      className: clsu.join(" "), containerStyle: styl,
      // eslint-disable-next-line max-len
      buttons: ["eraser", "copyformat", "undo", "redo", "brush", "bold", "italic", "underline", "strikethrough", "align", "indent", "outdent", "ul", "ol", "font", "fontsize", "lineHeight", "paragraph", "link", "image", "table", "hr", "print", "fullsize", "source"]

      // Fix cursor position bug:
      // events: {
      //   click: saveCursorPosition,
      //   keydown: saveCursorPosition,
      //   keyup: restoreCursorPosition,
      //   focus: restoreCursorPosition
      // }
      // i18n: (lng === "ru" ? "ru" : "en")
      // debugLanguage: true,
      // width: "100%", height: "100%",
      // commandToHotkeys: {
      //   bold: "ctrl+b", italic: "ctrl+i", underline: "ctrl+u", selectall: "ctrl+a", undo: "ctrl+z", redo: "ctrl+y,ctrl+shift+z",
      //   removeFormat: "ctrl+shift+m", openSearchDialog: "ctrl+f", openReplaceDialog: "ctrl+h",
      //   indent: "ctrl+]", outdent: "ctrl+["
      // },
    };

    (attributes.type || "").toLowerCase().replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["readonly"].indexOf(tt)) { typ.readonly = true }
      else if (~["editable"].indexOf(tt)) { typ.readonly = false }
      else if (~["disabled"].indexOf(tt)) { typ.disabled = true }
      else if (~["inline"].indexOf(tt)) { typ.inline = true }
      else if (~["nostatusbar", "noStatusbar", "hideStatusbar"].indexOf(tt)) { typ.statusbar = false }
      else if (~["notoolbar", "noToolbar", "hideToolbar"].indexOf(tt)) { typ.toolbar = false }
      else if (~["useSplitMode"].indexOf(tt)) { typ.useSplitMode = true }
      else if (~["fullsize", "fullscreen"].indexOf(tt)) { typ.fullsize = true }
    });

    // Dinamic attributes:
    if (elState.attributes.readonly === "true" || elState.attributes.readOnly === "true" || elState.attributes.editable === "false") {
      typ.readonly = true; typ.buttons = [];
    }
    if (elState.attributes.fullsize) { typ.fullsize = (elState.attributes.fullsize === "true") }
    if (elState.attributes.fullscreen) { typ.fullsize = (elState.attributes.fullscreen === "true") }

    // Static attributes:
    if (attributes.zIndex || attributes.zindex) { typ.zIndex = Number(attributes.zIndex || attributes.zindex) }
    if (attributes.tabIndex || attributes.tabindex) { typ.tabIndex = Number(attributes.tabIndex || attributes.tabindex) }
    if (attributes.width) { typ.width = (rz.canbeNumber(attributes.width) ? Number(attributes.width) : attributes.width) }
    if (attributes.height) { typ.height = (rz.canbeNumber(attributes.height) ? Number(attributes.height) : attributes.height) }
    if (attributes.minHeight) { typ.minHeight = (rz.canbeNumber(attributes.minHeight) ? Number(attributes.minHeight) : attributes.minHeight) }
    if (attributes.maxHeight) { typ.maxHeight = (rz.canbeNumber(attributes.maxHeight) ? Number(attributes.maxHeight) : attributes.maxHeight) }

    if (attributes.toolbar) { typ.toolbar = (attributes.toolbar === "true") }
    if (attributes.statusbar) { typ.statusbar = (attributes.statusbar === "true") }

    if (attributes.inline) { typ.inline = (attributes.inline === "true") }

    // Exotic properties
    if (attributes.useSplitMode) { typ.useSplitMode = (attributes.useSplitMode === "true") }
    if (attributes.colorPickerDefaultTab) { typ.colorPickerDefaultTab = attributes.colorPickerDefaultTab }
    if (attributes.imageDefaultWidth) { typ.imageDefaultWidth = Number(attributes.imageDefaultWidth) }

    if (attributes.buttons) { typ.buttons = ((attributes.buttons || "").replace(";", ",").split(",")).map((bb) => (bb.trim())).filter((bb) => (bb)) }

    if (attributes.extraButtons) {
      const jsna = attributes.extraButtons;
      if (jsna || rz.isString(jsna)) {
        const jsn = rz.objFromJSON((rz.strReplaceAll(jsna, "'", "\"")).trim());
        typ.extraButtons = jsn.map((jj:{[key:string]:any}) => ({
          name: jj.name, tooltip: rz.strFromSelectedLng({lng: lng as string, ru: jj.tooltip_ru, en: jj.tooltip_en, tr: jj.tooltip_tr, def: jj.tooltip}), iconURL: jj.iconURL,
          exec: (jeditor:any, current:any, control:any) => { const {value} = jeditor; extraButtonClick({btnName: jj.name, fncName: jj.extraButtonClick, jeditor, current, control, value}) }
        }));
      }
    }

    if (attributes.removeButtons) { typ.removeButtons = ((attributes.removeButtons || "").replace(";", ",").split(",")).map((bb) => (bb.trim().toLowerCase())).filter((bb) => (bb)) }
    if (attributes.disablePlugins) { typ.disablePlugins = ((attributes.disablePlugins || "").replace(";", ",").split(",")).map((bb) => (bb.trim().toLowerCase())).filter((bb) => (bb)) }
    if (attributes.extraPlugins) { typ.extraPlugins = ((attributes.extraPlugins || "").replace(";", ",").split(",")).map((bb) => (bb.trim().toLowerCase())).filter((bb) => (bb)) }

    if (attributes.limitWords) { typ.limitWords = Number(attributes.limitWords) }
    if (attributes.limitChars) { typ.limitChars = Number(attributes.limitChars) }
    if (attributes.defaultLineHeight) { typ.defaultLineHeight = Number(attributes.defaultLineHeight) }
    if (attributes.defaultActionOnPasteFromWord) { typ.defaultActionOnPasteFromWord = attributes.defaultActionOnPasteFromWord }
    if (attributes.tableAllowCellResize) { typ.tableAllowCellResize = (attributes.tableAllowCellResize === "true") }
    if (attributes.showXPathInStatusbar) { typ.showXPathInStatusbar = (attributes.showXPathInStatusbar === "true") }

    if (attributes.enableDragAndDropFileToEditor) { typ.enableDragAndDropFileToEditor = (attributes.enableDragAndDropFileToEditor === "true") }
    if (attributes.toolbarAdaptive) { typ.toolbarAdaptive = (attributes.toolbarAdaptive === "true") }
    if (attributes.toolbarSticky) { typ.toolbarSticky = (attributes.toolbarSticky === "true") }
    if (attributes.addNewLine) { typ.addNewLine = (attributes.addNewLine === "true") }
    if (attributes.useSearch) { typ.useSearch = (attributes.useSearch === "true") }

    // console.log({jconfig: typ});
    return typ;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes, elState.disabled, elState.attributes, permissions.disabled, placeholder, isCompact, theme, lng, clsu, styl, extraButtonClick]);

  // Set Value to global state
  const onBlur = useCallback((newContent) => {
    if (jconfig.readonly) { return }
    const value = newContent; // cleanHtml(newContent)
    if (fnc.config?.isDev) console.log({action: "<jodit key='" + key + "'> -> onBlur()", value});
    // @ts-ignore
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {value}}));

    const attr = (attributes.onBlur || attributes.onblur || attributes.onChange || attributes.onchange);
    if (rootNamespace && attr && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret && ret.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret && ret.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [jconfig.readonly, key, dispatch, rootAttributes.key, fullPath, attributes, rootNamespace, user, lng, dct]);

  // Restore the Cursor position after any change of jodit-textarea
  const onChange = useCallback(() => {
    if (jconfig.readonly) { return }
    if (editor.current) {
      // @ts-ignore
      const selection = editor.current.s.save();
      const startId = (selection && selection[0] && selection[0].startId) || "";
      // console.log(startId);
      if (startId) {
        setTimeout((sel) => {
        // @ts-ignore
          if (editor.current) { editor.current.s.restore(sel) }
        }, 100, selection);
      }
    }
  }, [jconfig.readonly]);

  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      // eslint-disable-next-line max-len
      return (
        <Form.Item {...frmItemProps}>
          {mainComponent}
        </Form.Item>
      )
    }
    return mainComponent;
  }, [isParentForm, title2, key, elState?.visible, permissions.visible]);

  // console.log(jconfig);

  return wrapperForm(
    <JoditEditor
      key={key} id={key} className={clsu.join(" ")} uk-tooltip={tooltip}
      config={jconfig}
      ref={editor}
      onBlur={onBlur}
      onChange={onChange}
    />
  );
}

export default RZJoditEditor;
/* eslint-enable @typescript-eslint/no-unused-vars */
